import { useMemo, useState } from 'react';
import { Roles } from '@touristation/shared';
import { useAuth } from '../auth/AuthProvider';
import { useCreateLeave, useDeleteLeave, useLeaves, useReviewLeave, type LeaveRequest } from '../api/hooks';
import { useT } from '../i18n/LangProvider';
import { Query } from '../ui/atoms';
import { Icon } from '../ui/Icon';
import { useConfirm } from '../ui/confirm';

const TYPE_LABEL: Record<string, string> = { ferie: 'Ferie', permesso: 'Permesso', malattia: 'Malattia' };
const TYPE_COLOR: Record<string, string> = { ferie: 'var(--info,#2b7bb9)', permesso: 'var(--t-orange)', malattia: 'var(--danger)' };
const STATUS_LABEL: Record<string, string> = { pending: 'In attesa', approved: 'Approvata', rejected: 'Rifiutata' };

const fmt = (iso: string) => new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' });
const todayIso = () => new Date().toISOString().slice(0, 10);

function periodLabel(l: LeaveRequest) {
  if (l.partial) return `${fmt(l.startDate)} · ${l.startTime}–${l.endTime}`;
  return l.startDate.slice(0, 10) === l.endDate.slice(0, 10) ? fmt(l.startDate) : `${fmt(l.startDate)} → ${fmt(l.endDate)}`;
}

function StatusPill({ s }: { s: LeaveRequest['status'] }) {
  const t = useT();
  const tone = s === 'approved' ? 'is-ok' : s === 'rejected' ? 'is-fail' : 'is-link';
  return <span className={`lrn-ltype ${tone}`} style={s === 'pending' ? { background: 'var(--warn-soft)', color: 'var(--warn)' } : undefined}>{t(STATUS_LABEL[s])}</span>;
}

function RequestForm({ onDone }: { onDone: () => void }) {
  const t = useT();
  const create = useCreateLeave();
  const [type, setType] = useState('ferie');
  const [partial, setPartial] = useState(false);
  const [startDate, setStartDate] = useState(todayIso());
  const [endDate, setEndDate] = useState(todayIso());
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('13:00');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    try {
      await create.mutateAsync({
        type,
        startDate,
        endDate: partial ? startDate : endDate,
        partial,
        ...(partial ? { startTime, endTime } : {}),
        reason: reason.trim() || undefined,
      });
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('Invio non riuscito.'));
    }
  }

  return (
    <div className="card" style={{ padding: 18, marginBottom: 20 }}>
      {error && <div style={{ background: '#fdecec', border: '1px solid #f3b6b6', color: '#8f2020', borderRadius: 8, padding: '9px 12px', marginBottom: 12, fontSize: 13 }}><Icon name="warn" size={13} /> {error}</div>}
      <div className="row" style={{ gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        <div>
          <label className="field-label">{t('Tipo')}</label>
          <select className="input" value={type} onChange={(e) => setType(e.target.value)}>
            <option value="ferie">{t('Ferie')}</option>
            <option value="permesso">{t('Permesso')}</option>
            <option value="malattia">{t('Malattia')}</option>
          </select>
        </div>
        <label className="row" style={{ gap: 8, alignItems: 'center', alignSelf: 'flex-end', paddingBottom: 8, fontSize: 13 }}>
          <input type="checkbox" checked={partial} onChange={(e) => setPartial(e.target.checked)} disabled={type !== 'permesso'} />
          {t('Permesso a ore (una giornata)')}
        </label>
      </div>

      {partial ? (
        <div className="row" style={{ gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
          <div><label className="field-label">{t('Giorno')}</label><input type="date" className="input" value={startDate} onChange={(e) => setStartDate(e.target.value)} /></div>
          <div><label className="field-label">{t('Dalle')}</label><input type="time" className="input" value={startTime} onChange={(e) => setStartTime(e.target.value)} /></div>
          <div><label className="field-label">{t('Alle')}</label><input type="time" className="input" value={endTime} onChange={(e) => setEndTime(e.target.value)} /></div>
        </div>
      ) : (
        <div className="row" style={{ gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
          <div><label className="field-label">{t('Dal')}</label><input type="date" className="input" value={startDate} onChange={(e) => { setStartDate(e.target.value); if (e.target.value > endDate) setEndDate(e.target.value); }} /></div>
          <div><label className="field-label">{t('Al')}</label><input type="date" className="input" value={endDate} min={startDate} onChange={(e) => setEndDate(e.target.value)} /></div>
        </div>
      )}

      <div style={{ marginBottom: 12 }}>
        <label className="field-label">{t('Motivo (opzionale)')}</label>
        <input className="input" placeholder={t('Es. viaggio, visita medica…')} value={reason} onChange={(e) => setReason(e.target.value)} />
      </div>
      <div className="cluster" style={{ justifyContent: 'flex-end' }}>
        <button className="btn btn--sm" onClick={onDone}>{t('Annulla')}</button>
        <button className="btn btn--sm btn--brand" onClick={() => void submit()} disabled={create.isPending}>{t('Invia richiesta')}</button>
      </div>
    </div>
  );
}

function LeaveRow({ l, canReview, showWho }: { l: LeaveRequest; canReview: boolean; showWho: boolean }) {
  const t = useT();
  const confirm = useConfirm();
  const review = useReviewLeave();
  const del = useDeleteLeave();
  const auth = useAuth();
  const mine = l.employeeId === auth.sub;

  return (
    <div className="card" style={{ padding: 14 }}>
      <div className="spaced" style={{ alignItems: 'flex-start' }}>
        <div className="row" style={{ gap: 10, alignItems: 'flex-start', minWidth: 0 }}>
          <span style={{ width: 8, alignSelf: 'stretch', borderRadius: 4, background: TYPE_COLOR[l.type], minHeight: 40 }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>
              {t(TYPE_LABEL[l.type])}{l.partial ? t(' a ore') : ''}
              {showWho && l.employee && <span className="muted" style={{ fontWeight: 400 }}> · {l.employee.name}</span>}
            </div>
            <div className="muted" style={{ fontSize: 12.5 }}>{periodLabel(l)}</div>
            {l.reason && <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>“{l.reason}”</div>}
            {l.status !== 'pending' && l.reviewedByName && (
              <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>
                {t(l.status === 'approved' ? 'Approvata da {by}' : 'Rifiutata da {by}', { by: l.reviewedByName })}{l.reviewNote ? ` — ${l.reviewNote}` : ''}
              </div>
            )}
          </div>
        </div>
        <div className="col" style={{ gap: 8, alignItems: 'flex-end' }}>
          <StatusPill s={l.status} />
          {canReview && l.status === 'pending' && (
            <div className="row" style={{ gap: 6 }}>
              <button className="btn btn--sm btn--brand" onClick={() => review.mutate({ id: l.id, status: 'approved' })}><Icon name="check" size={12} /> {t('Approva')}</button>
              <button className="btn btn--sm btn--danger" onClick={() => { const n = window.prompt(t('Motivo del rifiuto (opzionale):')) ?? undefined; review.mutate({ id: l.id, status: 'rejected', reviewNote: n }); }}>{t('Rifiuta')}</button>
            </div>
          )}
          {mine && l.status === 'pending' && (
            <button className="btn btn--sm" onClick={async () => { if (await confirm({ message: t('Ritirare la richiesta?'), confirmLabel: t('Ritira') })) del.mutate(l.id); }}>{t('Ritira')}</button>
          )}
        </div>
      </div>
    </div>
  );
}

export function LeaveScreen() {
  const t = useT();
  const auth = useAuth();
  const canReview = auth.hasRole(Roles.ADMIN) || auth.hasRole(Roles.TEAM_LEAD);
  const [tab, setTab] = useState<'mine' | 'approvals'>(canReview ? 'approvals' : 'mine');
  const [showForm, setShowForm] = useState(false);

  const mineQ = useLeaves({ scope: 'mine' });
  const allQ = useLeaves({ scope: 'all' });
  const pendingCount = useMemo(() => (allQ.data ?? []).filter((l) => l.status === 'pending').length, [allQ.data]);

  return (
    <div style={{ padding: 24 }}>
      <div className="page-head" style={{ marginBottom: 16 }}>
        <div>
          <h1 className="h1">{t('Ferie e permessi')}</h1>
          <div className="page-head__sub">{t('Richieste di assenza con approvazione. Le richieste approvate finiscono in calendario e nei turni.')}</div>
        </div>
        <button className="btn btn--brand" onClick={() => setShowForm((s) => !s)}><Icon name="plus" size={14} /> {t('Nuova richiesta')}</button>
      </div>

      {showForm && <RequestForm onDone={() => setShowForm(false)} />}

      {canReview && (
        <div className="lrn-tabs" style={{ marginBottom: 18 }}>
          <button className={`lrn-tab ${tab === 'approvals' ? 'is-active' : ''}`} onClick={() => setTab('approvals')}>
            <Icon name="check" size={14} /> {t('Da approvare')} {pendingCount > 0 && <span className="lrn-chips__n" style={{ background: 'var(--warn-soft)', color: 'var(--warn)' }}>{pendingCount}</span>}
          </button>
          <button className={`lrn-tab ${tab === 'mine' ? 'is-active' : ''}`} onClick={() => setTab('mine')}><Icon name="user" size={14} /> {t('Le mie richieste')}</button>
        </div>
      )}

      {tab === 'mine' || !canReview ? (
        <Query query={mineQ} empty={{ test: (d) => d.length === 0, title: t('Nessuna richiesta'), hint: t('Invia la tua prima richiesta di ferie o permesso.') }}>
          {(rows) => <div className="col" style={{ gap: 10 }}>{rows.map((l) => <LeaveRow key={l.id} l={l} canReview={false} showWho={false} />)}</div>}
        </Query>
      ) : (
        <Query query={allQ} empty={{ test: (d) => d.length === 0, title: t('Nessuna richiesta'), hint: t('Le richieste del team compaiono qui.') }}>
          {(rows) => {
            const pending = rows.filter((l) => l.status === 'pending');
            const handled = rows.filter((l) => l.status !== 'pending');
            return (
              <div className="col" style={{ gap: 18 }}>
                <div>
                  <h2 className="h2" style={{ fontSize: 14, marginBottom: 8 }}>{t('In attesa di approvazione')} ({pending.length})</h2>
                  {pending.length === 0 ? <div className="muted" style={{ fontSize: 13 }}>{t('Nessuna richiesta in attesa.')}</div>
                    : <div className="col" style={{ gap: 10 }}>{pending.map((l) => <LeaveRow key={l.id} l={l} canReview showWho />)}</div>}
                </div>
                {handled.length > 0 && (
                  <div>
                    <h2 className="h2" style={{ fontSize: 14, marginBottom: 8 }}>{t('Gestite')}</h2>
                    <div className="col" style={{ gap: 10 }}>{handled.slice(0, 30).map((l) => <LeaveRow key={l.id} l={l} canReview={false} showWho />)}</div>
                  </div>
                )}
              </div>
            );
          }}
        </Query>
      )}
    </div>
  );
}
